import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// MUI
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';

// Custom Components
import AppPage from '@/ui/AppPage/AppPage';
import DataCentricPrimaryTable from './DataCentricPrimaryTable';
import DataCentricSecondaryTable from './DataCentricSecondaryTable';
import DataCentricMiniMap from './DataCentricMiniMap';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { DialogFormGenerateFunction } from './DialogFormGenerateFunctionType';

import { IModel } from '@/@types/models/model';
import { Permission } from '@/@types/common';

import { IDialogProps } from '@/@types/ui/DialogContext';

export type DataCentricPageParams = {
  needPermission?: Array<Permission>;

  primaryModel: IModel;
  primaryTitleToken: string;
  primaryView: string | undefined;
  primaryAllowAdd?: boolean;
  primaryAddLabel?: string;
  primaryAllowView?: boolean;
  primaryAllowEdit?: boolean;
  primaryAllowGeneratePDF?: boolean;
  primaryAllowGenerateVodickiObrazac?: boolean;
  primaryDialogComponent?: React.FunctionComponent<IDialogProps>;
  primaryDialogForm?: string | DialogFormGenerateFunction;
  primaryDialogInsertForm?: string;
  primaryInitialSelectedId?: number;
  primaryOverrideAPIUrl?: string;
  primaryCanEditRecordAttribute?: string;
  primaryCanExportPdfRecordAttribute?: string;
  primaryAllowShareIzlet?: boolean;
  primaryExportLabel?: string;
  
  primaryAllowToggleJavno?: boolean;
  primaryAllowApprove?: boolean;
  primaryAllowToggleOdrzan?: boolean;
  primaryIzletiAdminFlagChangeType?: 'spv' | 'pu';

  secondaryModel: IModel;
  secondaryTitleToken: string;
  secondaryView: string | undefined;
  secondaryAllowAdd?: boolean;
  secondaryAddLabel?: string;
  secondaryAllowView?: boolean;
  secondaryAllowEdit?: boolean;
  secondaryDialogComponent?: React.FunctionComponent<IDialogProps>;
  secondaryDialogForm?: string | DialogFormGenerateFunction;
  secondaryDialogsUseCombinedPaths?: boolean;

  baseRecordPath: string;
  childRecordRelativePath: string;
  geomRelativePath: string;
  miniMapBaseRecordQueryParams?: string;

  mapId: number;
  mapModel: IModel;

  // Open page as dialog
  primaryDialogAsPagePath?: string | undefined;
};

function DataCentricPage(props: DataCentricPageParams) {
  const [selectedPrimaryRecordId, setSelectedPrimaryRecordId] = useState(props.primaryInitialSelectedId || 0);
  const [selectedSecondaryRecordId, setSelectedSecondaryRecordId] = useState(0);
  const [refreshSecondaryrecords, setRefreshSecondaryRecords] = useState(false);

  const mapRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const {
    needPermission,
    primaryModel,
    primaryView,
    primaryTitleToken,
    primaryAllowAdd,
    primaryAddLabel,
    primaryAllowView,
    primaryAllowEdit,
    primaryDialogComponent,
    primaryDialogForm,
    primaryDialogInsertForm,
    primaryInitialSelectedId,
    primaryDialogAsPagePath,
    primaryOverrideAPIUrl,
    primaryAllowGeneratePDF,
    primaryCanEditRecordAttribute,
    primaryCanExportPdfRecordAttribute,
    primaryAllowGenerateVodickiObrazac,
    primaryAllowShareIzlet,
    primaryExportLabel,
    primaryAllowToggleJavno,
    primaryAllowApprove,
    primaryAllowToggleOdrzan,
    primaryIzletiAdminFlagChangeType,
    secondaryModel,
    secondaryView,
    secondaryTitleToken,
    secondaryAllowAdd,
    secondaryAddLabel,
    secondaryAllowView,
    secondaryAllowEdit,
    secondaryDialogComponent,
    secondaryDialogForm,
    secondaryDialogsUseCombinedPaths,
    mapId,
    mapModel,
    baseRecordPath,
    childRecordRelativePath,
    geomRelativePath,
    miniMapBaseRecordQueryParams,
  } = props;

  const handlePrimaryRecordSelect = (id: number) => {
    if (id === selectedPrimaryRecordId || id === 0) {
      setSelectedPrimaryRecordId(0);
    } else if (id !== 0 && id !== null) {
      scrollIntoMap();
      setSelectedPrimaryRecordId(id);
    }
  };

  const handleSecondaryRecordSelect = (id: number) => {
    if (id !== selectedSecondaryRecordId && id !== 0 && id !== null) {
      setSelectedSecondaryRecordId(id);
    }
  };

  const handlePrimaryRecordsLoad = (records: Array<DCRecord>) => {
    setRefreshSecondaryRecords(true);
  };

  const handleSecondaryRecordsLoad = (records: Array<DCRecord>) => {};

  useEffect(() => {
    if (primaryInitialSelectedId) {
      setSelectedPrimaryRecordId(primaryInitialSelectedId);
    }
  }, [primaryInitialSelectedId]);

  const scrollIntoPrimary = () => {
    if (!mdUp && primaryRef.current) {
      primaryRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const scrollIntoMap = () => {
    if (!mdUp && mapRef.current) {
      mapRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  useEffect(() => {
    scrollIntoPrimary();
  }, [selectedPrimaryRecordId]);

  const [alternateView, setAlternateView] = useState(false);
  const handleMapResize = () => {
    setAlternateView((prev) => !prev);
  };

  useEffect(() => {
    const checkIfMiniMapWasResized = () => {
      const extendedMaps = localStorage.getItem('extended_mini_map');
      if (extendedMaps !== null) {
        const array = JSON.parse(extendedMaps);
        array.forEach((element: string) => {
          if (element.valueOf() === location.pathname.valueOf()) {
            setAlternateView(true);
            return;
          }
        });
      }
    };
    checkIfMiniMapWasResized();
  }, [location.pathname]);

  const showAlternate = alternateView && mdUp;
  const showMobile = !mdUp;

  return (
    <AppPage needPermission={needPermission}>
      <Box
        height={'100%'}
        width={'100%'}
        sx={{
          display: 'grid',
          gridAutoFlow: 'dense',
          gridTemplateColumns: showMobile ? '1fr' : showAlternate ? '1fr 2fr' : '20% 80%',
          gridTemplateRows: showMobile ? '55% 35% 40%' : showAlternate ? '1fr 1fr' : '60% 40%',
        }}
      >
        <div ref={mapRef} style={{ gridColumn: '1', gridRow: showAlternate ? '1 / span 2' : '2', height: '100%', overflow: 'hidden' }}>
          <DataCentricMiniMap
            onPrimaryRecordSelect={handlePrimaryRecordSelect}
            selectedPrimaryRecordId={selectedPrimaryRecordId}
            model={mapModel}
            mapId={mapId}
            baseRecordPath={baseRecordPath}
            geomRelativePath={geomRelativePath}
            miniMapBaseRecordQueryParams={miniMapBaseRecordQueryParams}
            isResized={alternateView}
            onResize={handleMapResize}
            url={useLocation().pathname}
          />
        </div>

        <div style={{ gridColumn: showAlternate ? '2' : '1 / span 2', gridRow: '1', height: '100%', overflow: 'hidden' }} ref={primaryRef}>
          <DataCentricPrimaryTable
            model={primaryModel}
            titleToken={primaryTitleToken}
            view={primaryView}
            onLoad={handlePrimaryRecordsLoad}
            onRowAction={() => {}}
            onRecordSelect={handlePrimaryRecordSelect}
            selectedRecordId={selectedPrimaryRecordId}
            allowAdd={primaryAllowAdd}
            addLabel={primaryAddLabel}
            allowView={primaryAllowView}
            allowEdit={primaryAllowEdit}
            allowGeneratePDF={primaryAllowGeneratePDF}
            allowCanEditRecordAttribute={primaryCanEditRecordAttribute}
            allowCanExportPdfRecordAttribute={primaryCanExportPdfRecordAttribute}
            allowGenerateVodickiObrazac={primaryAllowGenerateVodickiObrazac}
            allowShareIzlet={primaryAllowShareIzlet}
            dialogComponent={primaryDialogComponent}
            dialogForm={primaryDialogForm}
            dialogInsertForm={primaryDialogInsertForm}
            apiURL={primaryOverrideAPIUrl}
            baseRecordPath={baseRecordPath}
            dialogAsPagePath={primaryDialogAsPagePath}
            exportLabel={primaryExportLabel}
            allowToggleJavno={primaryAllowToggleJavno}
            allowApprove={primaryAllowApprove}
            allowToggleOdrzan={primaryAllowToggleOdrzan}
            izletiAdminFlagChangeType={primaryIzletiAdminFlagChangeType}
          />
        </div>
        <div
          style={{
            gridColumn: showMobile ? '1' : '2',
            gridRow: showMobile ? '3' : '2',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <DataCentricSecondaryTable
            selectedPrimaryRecordId={selectedPrimaryRecordId}
            onRecordSelect={handleSecondaryRecordSelect}
            onLoad={handleSecondaryRecordsLoad}
            onRowAction={() => {}}
            view={secondaryView}
            model={secondaryModel}
            titleToken={secondaryTitleToken}
            baseRecordPath={baseRecordPath}
            childRecordRelativePath={childRecordRelativePath}
            triggerRefresh={refreshSecondaryrecords}
            setTriggerRefresh={setRefreshSecondaryRecords}
            allowAdd={selectedPrimaryRecordId ? secondaryAllowAdd : false}
            addLabel={secondaryAddLabel}
            allowView={secondaryAllowView}
            allowEdit={secondaryAllowEdit}
            dialogComponent={secondaryDialogComponent}
            dialogForm={secondaryDialogForm}
            dialogsUseCombinedPaths={secondaryDialogsUseCombinedPaths}
          />
        </div>
      </Box>
    </AppPage>
  );
}

export default DataCentricPage;
